import { getObjectRefFlat } from 'Core/Hooks/useObjectRelations'
import _ from 'lodash'
import { TargetIcon } from 'procyon-ui'
import React from 'react'
import {
  AppHTTPEntryPathKey,
  GcpWFFAppPoolID,
  GcpWFFProviderID,
  ReadOnlyAppTypes,
  UIApplicationTypeIconMap
} from './constants'
import { getCurrentOrg } from 'Utils/Helpers'
import store from 'infra/redux/store'
/**
 * Returns URL which can be used for signin into the application
 * @param {object} app
 * @returns
 */
export const getApplicationSignInURL = (app) => {
  const user = JSON.parse(localStorage.getItem('user'))
  const tenantProfiles = Object.values(store.getState().tenantprofiles.map)
  const isAgentlessMode = user.awsurl !== 'signin.aws.procyon.ai'
  const profile = tenantProfiles.find((e) => e.ObjectMeta.Name === user.ObjectMeta.Tenant)
  const isInterOpModeEnabled = profile?.Spec.TunnelConfig.NoDnsProxyMode
  const port = isInterOpModeEnabled ? ':8643' : ''
  const appType = app.Spec.AppType
  let signInUrl
  switch (appType) {
    case 'github':
      signInUrl = _.get(app, 'Spec.SamlServiceProvider.ServiceProviderACSURL').replace(
        /saml$/,
        'sso'
      )
      break

    case 'salesforce':
      signInUrl = _.get(app, 'Spec.SalesforceAppConfig.InstanceURL')
      break

    case 'snowflake':
      if (isInterOpModeEnabled) {
        signInUrl = `https://${user.tenant}.saml.${user.controller}${port}/saml/application?Type=snowflake&Name=${app.ObjectMeta.Name}`
      } else {
        signInUrl = `https://${user.tenant}.saml.${user.controller}/saml/application?Type=snowflake&Name=${app.ObjectMeta.Name}`
      }
      break
    case 'databricks':
      if (isInterOpModeEnabled) {
        signInUrl = `https://${user.tenant}.saml.${user.controller}${port}/saml/application?Type=databricks&Name=${app.ObjectMeta.Name}`
      } else {
        signInUrl = `https://${user.tenant}.saml.${user.controller}/saml/application?Type=databricks&Name=${app.ObjectMeta.Name}`
      }
      break
    case 'gcp-federation':
      return getGCPWFFederationAppSignInURL(app)
    default:
      // Get HTTP Entry Path
      const entryPath = _.get(app, AppHTTPEntryPathKey, '/')
      const appSignInUrl =
        _.get(app, 'Spec.SamlServiceProvider.ServiceProviderACSURL') ||
        `https://${_.get(app, 'Spec.HttpAppConfig.FrontEndName.Elems[0]', '')}${entryPath}`
      const agentLessSignInUrl = appSignInUrl
        .replace('.default', '-app-default')
        .replace('procyon.ai', user.rdpurl)
      signInUrl = isAgentlessMode ? agentLessSignInUrl : appSignInUrl
      break
  }
  return signInUrl
}

export const getApplicationIcon = (app) => {
  const type = getApplicationType(app)
  //@ts-ignore
  const Icon = ({ ...rest }) => <TargetIcon type={UIApplicationTypeIconMap[type]} {...rest} />
  return Icon
}

/**
 * Returns the type of application
 * @param {*} app
 * @returns {'AWS'|'AZURE'|'GCP'|'DROPBOX'|'HTTP'|'GITHUB'|'SALESFORCE'|'gcp-federation'|'MCP'} Application Type
 */
export const getApplicationType = (app) => {
  if (!app) return ''
  const account = getObjectRefFlat(app?.Spec?.Account)
  if (account) return account.Spec.Type
  if (app?.Spec?.AppType) return app.Spec.AppType.toUpperCase()
  return app?.ObjectMeta?.Kind || ''
}

/**
 * Get the name from frontend url
 * @param {string} fnURL The frontend url in format: <some name>.applications.procyon.ai
 * @returns the <some name> from `fnURL`
 */
export const getFrontendNameFromURL = (fnURL = '') => {
  const withoutAppDomain = fnURL
    .replace(`.${getCurrentOrg()}.applications.procyon.ai`, '')
    .replace('https://', '')
    .replace(/:\d+$/, '')

  return withoutAppDomain
}

/**
 * Returns `true` if this app is readonly - cannot be edited or deleted
 */
export const isReadOnlyApp = (app) => {
  const type = getApplicationType(app)
  if (type === 'gcp-federation') {
    // GCP Federation config account/WF Fed Account
    if (app.Spec.GCPFederationConfig.PoolID && app.Spec.GCPFederationConfig.ProviderID) {
      return true
    }
  }

  return !!ReadOnlyAppTypes[type]
}

export const getApplicationHostName = (application) => {
  const FrontendName = _.get(application, 'Spec.HttpAppConfig.FrontEndName.Elems', [])[0]
  const SAMLACSURL = _.get(application, 'Spec.SamlServiceProvider.ServiceProviderACSURL')
  return FrontendName || SAMLACSURL
}

export const getGCPWFFederationAppSignInURL = (app) => {
  const poolID = _.get(app, GcpWFFAppPoolID, '')
  const providerID = _.get(app, GcpWFFProviderID, '')
  return `https://auth.cloud.google/signin/locations/global/workforcePools/${poolID}/providers/${providerID}?continueUrl=https://console.cloud.google/`
}

export function checkValidHostname(name) {
  // Normalize input
  name = name.trim().toUpperCase()

  // // Rule 1: Max length 24
  // if (name.length > 24) {
  //   return 'Hostname must not exceed 24 characters.'
  // }

  // Rule 2: No spaces allowed
  if (/\s/.test(name)) {
    return 'Hostname must not contain spaces.'
  }

  // Rule 3: Allowed characters only (A-Z, 0-9, -, .)
  if (!/^[A-Z0-9.-]+$/.test(name)) {
    return "Hostname contains invalid characters. Only A-Z, 0-9, '-', and '.' are allowed."
  }

  // Rule 4: First character must be a letter
  if (!/^[A-Z]/.test(name)) {
    return 'Hostname must start with a letter (A-Z).'
  }

  // Rule 5: Last character must not be '-' or '.'
  if (/[-.]$/.test(name)) {
    return 'Hostname must not end with a hyphen or period.'
  }

  // Rule 6: Single-character names not allowed
  if (name.length === 1) {
    return 'Hostname must be at least two characters.'
  }

  // Rule 7: Periods only used to separate domain components (no consecutive or trailing)
  if (/\.\./.test(name)) {
    return 'Hostname must not contain consecutive periods.'
  }

  return '' // All checks passed
}

export const WithTooltip = ({ id, content, children }) => {
  return (
    <div className='relative inline-block group' aria-describedby={id}>
      <button type='button' className='outline-none' aria-describedby={id}>
        {children}
      </button>
      <div
        id={id}
        role='tooltip'
        className='
          pointer-events-none
          absolute left-1/2 -translate-x-1/2 mt-2
          hidden group-hover:block group-focus-within:block
          whitespace-pre-line z-50 rounded-md px-2 py-1 text-xs
          bg-gray-900 text-white shadow-lg w-max
        '
      >
        {content}
        <span
          aria-hidden
          className='absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-gray-900'
        />
      </div>
    </div>
  )
}
